import React, { useState, useEffect } from 'react';
import { Storage } from 'aws-amplify';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import RichTextEditor from 'components/form/RichTextEditor';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import InfoIcon from '@material-ui/icons/Info';
import SendIcon from '@material-ui/icons/Send';
import ReplyIcon from '@material-ui/icons/Reply';
import DeleteIcon from '@material-ui/icons/Delete';
import Spin from 'components/common/Spin';
import Button from 'components/common/Button';
import Dropzone from 'components/form/Dropzone';
import uuidv4 from 'uuidv4';
import useSnack from "hooks/useSnack";

const BUCKET = process.env.REACT_APP_BUCKET;

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  inputContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    position: 'relative',
  },
  textField: {
    '& .MuiInputBase-root': {
      borderRadius: 30,
      paddingRight: 40,
      paddingLeft: 16,
    },
  },
  btn: {
    position: 'absolute',
    right: 10,
    marginBottom: 7,
  },
  center: {
    textAlign: 'center',
  },
  signature: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 8,
  },
  appBar: {
    marginTop: theme.spacing(1),
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
}));

const Composer = ({ onSend, type, convoId, user }) => {
  const classes = useStyles();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showEmailEditor, setShowEmailEditor] = useState();
  const [attachments, setAttachments] = useState([]);
  const snack = useSnack();

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const files = attachments.length && await uploadFiles();
      const sent = await onSend(value, null, files);
      if (sent) {
        setValue('');
        setAttachments([]);
        setShowEmailEditor(false);
        localStorage.removeItem(`draft_${convoId}`);
      }
    } catch (e) {
      snack.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const draft = localStorage.getItem(`draft_${convoId}`);
    if (draft) {
      setValue(draft);
    }
  }, []);

  const onChange = (val) => {
    setValue(val);
    localStorage.setItem(`draft_${convoId}`, val);
  }

  const handleUpload = (files) => {
    setAttachments(files);
  };

  const onRemove = (file) => {
    setAttachments(attachments.filter((a) => a.name !== file.name));
  };

  const uploadFiles = async () => {
    return  await Promise.all(
      attachments.map((file) => {
        const key = uuidv4() + '-' + file.name;
        const contentType = file.type;

        return Storage.put(key, file, {
          contentType,
        }).then((result) => {
          const url = `https://${BUCKET}.s3.us-east-2.amazonaws.com/public/${result.key}`;
          return {
            url,
            name: file.name,
            size: file.size
          };
        });
      }),
    );
  };

  if (type === 'email') {
    return (
      <div className={classes.root}>
        <div>
          {showEmailEditor ? (
            <div>
              <RichTextEditor
                autoFocus
                name="htmlmessage"
                placeholder="Start your message..."
                mceOptions={{
                  options: {
                    min_height: 300,
                    max_height: 600,
                  },
                  plugins: [],
                }}
                onChange={onChange}
                value={value}
              />
              <Dropzone
                multiple
                accept=""
                id="attachments"
                onUpload={handleUpload}
                onRemove={onRemove}
              />
              <AppBar
                position="relative"
                className={classes.appBar}
                elevation={0}
                color="transparent"
              >
                <Toolbar disableGutters variant="dense">
                  <Box flexGrow={1}>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!value}
                      startIcon={<SendIcon />}
                      color="primary"
                    >
                      Send
                    </Button>
                  </Box>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={() => setShowEmailEditor(false)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Toolbar>
              </AppBar>
            </div>
          ) : (
            <Button
              variant="outlined"
              startIcon={<ReplyIcon />}
              color="primary"
              onClick={() => setShowEmailEditor(true)}
            >
              {value ? 'Continue composing' : 'Reply'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Grid sm={12} item className={classes.inputContainer}>
        <TextField
          multiline
          fullWidth
          rowsMax={10}
          margin="none"
          size="small"
          variant="outlined"
          classes={{
            root: classes.textField,
          }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <IconButton
          disabled={saving || !value}
          className={classes.btn}
          size="small"
          onClick={handleSubmit}
        >
          {saving ? <Spin spinning /> : <SendIcon color="primary" />}
        </IconButton>
      </Grid>
      <Grid container spacing={1}>
        <Grid item sm={12}>
          <Typography variant="caption" className={classes.signature}>
            - {user.fname}{' '}
            <Tooltip title="Your name will be added to signature of the message">
              <InfoIcon
                color={'action'}
                style={{ fontSize: 16, marginLeft: 4 }}
              />
            </Tooltip>
          </Typography>
        </Grid>
      </Grid>
    </div>
  );
};

export default Composer;
